import { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Plus,
  Clock,
  MoreHorizontal,
  RotateCcw,
  Copy,
  Trash2,
  Camera,
  CalendarClock,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Play,
  Pause,
} from 'lucide-react';
import type { Volume, VolumeSnapshot, SnapshotSchedule } from '@/service/gpu-instance/volume-type';
import { SnapshotStatus } from '@/service/gpu-instance/volume-type';
import {
  useListSnapshots,
  useListSchedules,
  useDeleteSnapshot,
  useToggleSchedule,
  useDeleteSchedule,
} from '@/service/gpu-instance/volume-hook';
import {
  CreateSnapshotDialog,
  RestoreSnapshotDialog,
  ScheduleSnapshotDialog,
} from '../../../components/volume';
import { toast } from 'sonner';

interface OutletContextType {
  volume: Volume | undefined;
  isLoading: boolean;
}

const snapshotStatusConfig: Record<
  SnapshotStatus,
  { label: string; icon: typeof CheckCircle2; className: string }
> = {
  [SnapshotStatus.Ready]: {
    label: 'Ready',
    icon: CheckCircle2,
    className: 'text-green-600 bg-green-50 dark:bg-green-950/30',
  },
  [SnapshotStatus.Creating]: {
    label: 'Creating',
    icon: Loader2,
    className: 'text-blue-600 bg-blue-50 dark:bg-blue-950/30',
  },
  [SnapshotStatus.Deleting]: {
    label: 'Deleting',
    icon: Loader2,
    className: 'text-orange-600 bg-orange-50 dark:bg-orange-950/30',
  },
  [SnapshotStatus.Restoring]: {
    label: 'Restoring',
    icon: Loader2,
    className: 'text-purple-600 bg-purple-50 dark:bg-purple-950/30',
  },
  [SnapshotStatus.Error]: {
    label: 'Error',
    icon: AlertCircle,
    className: 'text-red-600 bg-red-50 dark:bg-red-950/30',
  },
};

const VolumeSnapshots = () => {
  const { volume, isLoading: volumeLoading } = useOutletContext<OutletContextType>();

  // Dialog states
  const [createOpen, setCreateOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [restoreSnapshot, setRestoreSnapshot] = useState<VolumeSnapshot | null>(null);
  const [deleteSnapshotConfirm, setDeleteSnapshotConfirm] = useState<VolumeSnapshot | null>(null);
  const [deleteScheduleConfirm, setDeleteScheduleConfirm] = useState<SnapshotSchedule | null>(null);

  // Fetch data
  const { data: snapshots = [], isLoading: snapshotsLoading, refetch: refetchSnapshots } = useListSnapshots(volume?.id);
  const { data: schedules = [], isLoading: schedulesLoading, refetch: refetchSchedules } = useListSchedules(volume?.id);

  // Mutations
  const deleteSnapshotMutation = useDeleteSnapshot();
  const toggleScheduleMutation = useToggleSchedule();
  const deleteScheduleMutation = useDeleteSchedule();

  const isLoading = volumeLoading || snapshotsLoading || schedulesLoading;

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatRelativeTime = (timestamp: number) => {
    const diff = Date.now() - timestamp;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  const handleDeleteSnapshot = async () => {
    if (!deleteSnapshotConfirm) return;
    try {
      await deleteSnapshotMutation.mutateAsync(deleteSnapshotConfirm.id);
      toast.success('Snapshot deleted');
      refetchSnapshots();
    } catch {
      toast.error('Failed to delete snapshot');
    }
    setDeleteSnapshotConfirm(null);
  };

  const handleToggleSchedule = async (schedule: SnapshotSchedule) => {
    try {
      await toggleScheduleMutation.mutateAsync({
        scheduleId: schedule.id,
        enabled: !schedule.enabled,
      });
      toast.success(schedule.enabled ? 'Schedule disabled' : 'Schedule enabled');
      refetchSchedules();
    } catch {
      toast.error('Failed to update schedule');
    }
  };

  const handleDeleteSchedule = async () => {
    if (!deleteScheduleConfirm) return;
    try {
      await deleteScheduleMutation.mutateAsync(deleteScheduleConfirm.id);
      toast.success('Schedule deleted');
      refetchSchedules();
    } catch {
      toast.error('Failed to delete schedule');
    }
    setDeleteScheduleConfirm(null);
  };

  if (!volume) {
    return (
      <div className="space-y-6">
        {[...Array(2)].map((_, i) => (
          <Skeleton key={i} className="h-64 w-full" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Snapshot Schedules */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <CalendarClock className="h-4 w-4" />
              Snapshot Schedules
            </CardTitle>
            <CardDescription>Automated snapshot creation policies</CardDescription>
          </div>
          <Button size="sm" onClick={() => setScheduleOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            New Schedule
          </Button>
        </CardHeader>
        <CardContent>
          {schedulesLoading ? (
            <div className="space-y-3">
              {[...Array(2)].map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : schedules.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <Clock className="h-8 w-8 text-muted-foreground/50 mb-2" />
              <p className="text-sm text-muted-foreground">No schedules configured</p>
              <Button
                variant="outline"
                size="sm"
                className="mt-2"
                onClick={() => setScheduleOpen(true)}
              >
                Create Schedule
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {schedules.map((schedule) => (
                <div
                  key={schedule.id}
                  className="flex items-center justify-between rounded-lg border p-4"
                >
                  <div className="flex items-center gap-4">
                    <Switch
                      checked={schedule.enabled}
                      onCheckedChange={() => handleToggleSchedule(schedule)}
                    />
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{schedule.name}</span>
                        <Badge variant="outline" className="text-xs capitalize">
                          {schedule.frequency}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Keep {schedule.retentionCount} snapshots
                        {schedule.cronExpression && ` | ${schedule.cronExpression}`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right text-sm">
                      {schedule.lastRun && (
                        <p className="text-muted-foreground">
                          Last: {formatRelativeTime(schedule.lastRun)}
                        </p>
                      )}
                      <p className="text-muted-foreground">
                        Next: {formatDate(schedule.nextRun)}
                      </p>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleToggleSchedule(schedule)}>
                          {schedule.enabled ? (
                            <>
                              <Pause className="mr-2 h-4 w-4" />
                              Disable
                            </>
                          ) : (
                            <>
                              <Play className="mr-2 h-4 w-4" />
                              Enable
                            </>
                          )}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => setDeleteScheduleConfirm(schedule)}
                          className="text-destructive"
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Snapshots List */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Camera className="h-4 w-4" />
              Snapshots
            </CardTitle>
            <CardDescription>
              {snapshots.length} snapshot{snapshots.length !== 1 ? 's' : ''} available
            </CardDescription>
          </div>
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Create Snapshot
          </Button>
        </CardHeader>
        <CardContent>
          {snapshotsLoading ? (
            <div className="space-y-3">
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : snapshots.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Camera className="h-12 w-12 text-muted-foreground/50 mb-4" />
              <h3 className="text-lg font-medium">No snapshots yet</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Create a snapshot to capture the current state of your volume.
              </p>
              <Button onClick={() => setCreateOpen(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Create First Snapshot
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Size</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="w-12" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {snapshots.map((snapshot) => {
                  const statusConfig = snapshotStatusConfig[snapshot.status];
                  const StatusIcon = statusConfig.icon;
                  const isSpinning = [
                    SnapshotStatus.Creating,
                    SnapshotStatus.Deleting,
                    SnapshotStatus.Restoring,
                  ].includes(snapshot.status);

                  return (
                    <TableRow key={snapshot.id}>
                      <TableCell>
                        <div>
                          <span className="font-medium">{snapshot.name}</span>
                          {snapshot.description && (
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {snapshot.description}
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={statusConfig.className}>
                          <StatusIcon
                            className={`mr-1 h-3 w-3 ${isSpinning ? 'animate-spin' : ''}`}
                          />
                          {statusConfig.label}
                        </Badge>
                      </TableCell>
                      <TableCell>{snapshot.size} GB</TableCell>
                      <TableCell>
                        {snapshot.isScheduled ? (
                          <Badge variant="secondary" className="text-xs">
                            <Clock className="mr-1 h-3 w-3" />
                            {snapshot.scheduleName || 'Scheduled'}
                          </Badge>
                        ) : (
                          <span className="text-sm text-muted-foreground">Manual</span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatRelativeTime(snapshot.createdAt)}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => setRestoreSnapshot(snapshot)}
                              disabled={!snapshot.restorable}
                            >
                              <RotateCcw className="mr-2 h-4 w-4" />
                              Restore
                            </DropdownMenuItem>
                            <DropdownMenuItem disabled={!snapshot.restorable}>
                              <Copy className="mr-2 h-4 w-4" />
                              Clone to New Volume
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => setDeleteSnapshotConfirm(snapshot)}
                              className="text-destructive"
                              disabled={snapshot.status === SnapshotStatus.Creating}
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Dialogs */}
      <CreateSnapshotDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        volume={volume}
        onSuccess={() => refetchSnapshots()}
      />

      <ScheduleSnapshotDialog
        open={scheduleOpen}
        onOpenChange={setScheduleOpen}
        volume={volume}
        onSuccess={() => refetchSchedules()}
      />

      <RestoreSnapshotDialog
        open={!!restoreSnapshot}
        onOpenChange={(open) => !open && setRestoreSnapshot(null)}
        snapshot={restoreSnapshot}
        onSuccess={() => refetchSnapshots()}
      />

      {/* Delete Snapshot Confirmation */}
      <AlertDialog
        open={!!deleteSnapshotConfirm}
        onOpenChange={(open) => !open && setDeleteSnapshotConfirm(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Snapshot</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the snapshot "{deleteSnapshotConfirm?.name}"? This
              action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteSnapshot}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Schedule Confirmation */}
      <AlertDialog
        open={!!deleteScheduleConfirm}
        onOpenChange={(open) => !open && setDeleteScheduleConfirm(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Schedule</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the schedule "{deleteScheduleConfirm?.name}"? Existing
              snapshots will not be affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteSchedule}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default VolumeSnapshots;
